import { datadogLogs } from '@datadog/browser-logs';
import { type Zero } from '@rocicorp/zero';
import type { Schema } from '@strength/zero-schema';

// Holds the live Zero instance so logout — which runs outside ZeroProvider's
// React context (see cleanupSessions in auth.tsx) — can tear it down.
let activeZero: Zero<Schema> | null = null;

/**
 * Registers the live Zero instance for logout teardown (see {@link closeActiveZero}).
 * Not cleared on unmount; closeActiveZero clears the reference after closing.
 */
export function setActiveZero(zero: Zero<Schema> | null): void {
  activeZero = zero;
}

/**
 * On logout/session expiry, stops the active Zero instance from syncing but KEEPS
 * its IndexedDB data. `close()` shuts the instance down without deleting the local
 * cache, so the next login for the same user (same storageKey) rehydrates from the
 * warm cache instead of re-syncing from scratch.
 *
 * We deliberately do not call `delete()`/`dropAllDatabases()`. This is acceptable
 * only because the cached data is non-sensitive (workout data). Zero names each
 * store by `userID` + `storageKey`, so the app never surfaces one account's cached
 * rows under another. That is not a security or privacy boundary, though: the rows
 * still sit in IndexedDB on the device. If sensitive data is ever cached, wipe on
 * logout (`delete()`/`dropAllDatabases()`) or use in-memory storage (`kvStore: 'mem'`).
 */
export async function closeActiveZero(): Promise<void> {
  const zero = activeZero;
  activeZero = null;
  try {
    await zero?.close();
  } catch (error) {
    datadogLogs.logger.error('Error closing active Zero instance on logout', {
      error,
    });
  }
}
